import { useCallback, useEffect, useRef, useState } from "react";
import { getSmartGlocalToken, createSbpSession, getSbpSessionStatus } from "../Api";
import { STORAGE_KEYS } from "../constants";
import type { ShopItem } from "../types";
import { setStorageItem } from "../utils";

export function useProductPayment() {
  const [activeProduct, setActiveProduct] = useState<ShopItem | null>(null);
  const [activePlayerId, setActivePlayerId] = useState<number | null>(null);
  const [isMethodSelectorOpen, setIsMethodSelectorOpen] = useState(false);
  const [isPaymentStarting, setIsPaymentStarting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const [cardPaymentKey, setCardPaymentKey] = useState(0);
  const [isCardOverlayOpen, setIsCardOverlayOpen] = useState(false);
  const paymentFormRef = useRef<SmglPaymentFormInstance | null>(null);

  const cleanupCardWidget = useCallback(() => {
    paymentFormRef.current?.destroy?.();
    paymentFormRef.current = null;
  }, []);

  const closePaymentOverlay = useCallback(() => {
    cleanupCardWidget();
    setIsCardOverlayOpen(false);
    setIsMethodSelectorOpen(false);
    setIsPaymentStarting(false);
    setPaymentError(null);
    setActiveProduct(null);
    setActivePlayerId(null);
  }, [cleanupCardWidget]);

  const openMethodSelector = useCallback((product: ShopItem, playerId: number) => {
    setActiveProduct(product);
    setActivePlayerId(playerId);
    setPaymentError(null);
    setIsMethodSelectorOpen(true);
  }, []);

  const closeMethodSelector = useCallback(() => {
    setIsMethodSelectorOpen(false);
    setActiveProduct(null);
    setActivePlayerId(null);
    setPaymentError(null);
  }, []);

  useEffect(
    () => () => {
      cleanupCardWidget();
    },
    [cleanupCardWidget],
  );

  useEffect(() => {
    if (!isCardOverlayOpen && !isMethodSelectorOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (isPaymentStarting) return;
      if (isCardOverlayOpen) {
        closePaymentOverlay();
      } else if (isMethodSelectorOpen) {
        closeMethodSelector();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeMethodSelector, closePaymentOverlay, isCardOverlayOpen, isMethodSelectorOpen, isPaymentStarting]);

  const startCardPayment = useCallback(async () => {
    if (isPaymentStarting) return;
    if (!activeProduct || activePlayerId === null) return;

    const product = activeProduct;
    const playerId = activePlayerId;

    const SmglPaymentForm = window.SmglPaymentForm;
    if (!SmglPaymentForm) {
      setPaymentError("Не удалось загрузить форму оплаты. Обновите страницу и попробуйте снова.");
      return;
    }

    cleanupCardWidget();
    localStorage.removeItem(STORAGE_KEYS.sbpSession);

    setIsPaymentStarting(true);
    setPaymentError(null);

    setStorageItem(
      STORAGE_KEYS.cardSession,
      JSON.stringify({
        playerId,
        productId: product.id,
        timestamp: Date.now(),
      }),
    );

    const token = await getSmartGlocalToken(product.id, playerId, "https://durakonline3d.ru/#/payment-status");
    if (!token) {
      setIsPaymentStarting(false);
      setPaymentError("Не удалось загрузить форму оплаты. Проверьте подключение и попробуйте ещё раз.");
      return;
    }

    setIsMethodSelectorOpen(false);
    setCardPaymentKey((prev) => prev + 1);
    setIsCardOverlayOpen(true);

    setTimeout(() => {
      const mountNode = document.getElementById("smgl-payment-form");
      if (!mountNode) {
        setPaymentError("Ошибка загрузки формы оплаты.");
        closePaymentOverlay();
        return;
      }

      try {
        const form = new SmglPaymentForm(token, {
          isCvcMasked: true,
          customerInteractionRedirect: { target: "_self" },
        });

        form.onPaymentSuccess = () => {
          setStorageItem(STORAGE_KEYS.lastPlayerId, String(playerId));
          localStorage.removeItem(STORAGE_KEYS.cardSession);
          closePaymentOverlay();
        };
        form.onPaymentFail = () => {
          setPaymentError("Произошла ошибка при оплате. Проверьте данные карты и попробуйте ещё раз.");
          closePaymentOverlay();
        };
        form.onDestroy = () => {
          closePaymentOverlay();
        };

        paymentFormRef.current = form;
        form.render();
        setIsPaymentStarting(false);
      } catch {
        setPaymentError("Не удалось создать форму оплаты. Попробуйте ещё раз.");
        setIsPaymentStarting(false);
        closePaymentOverlay();
      }
    }, 100);
  }, [activePlayerId, activeProduct, cleanupCardWidget, closePaymentOverlay, isPaymentStarting]);

  const startSbpPayment = useCallback(async () => {
    if (isPaymentStarting) return;
    if (!activeProduct || activePlayerId === null) return;

    const product = activeProduct;
    const playerId = activePlayerId;

    cleanupCardWidget();
    setIsCardOverlayOpen(false);
    localStorage.removeItem(STORAGE_KEYS.cardSession);

    setIsPaymentStarting(true);
    setPaymentError(null);

    const session = await createSbpSession(product.id, playerId, "https://durakonline3d.ru/#/payment-status");
    if (!session) {
      setIsPaymentStarting(false);
      setPaymentError("Не удалось создать платёж по СБП. Проверьте подключение и попробуйте ещё раз.");
      return;
    }

    setStorageItem(
      STORAGE_KEYS.sbpSession,
      JSON.stringify({
        sessionId: session.sessionId,
        playerId,
        productId: product.id,
        timestamp: Date.now(),
      }),
    );

    let qrContent = session.qrContent;
    let lastStatus = session.status;

    for (let attempt = 0; !qrContent && attempt < 15; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 1000));
      const status = await getSbpSessionStatus(session.sessionId);
      if (!status) continue;
      lastStatus = status.status;
      if (status.status === "failed") break;
      if (status.qrContent) {
        qrContent = status.qrContent;
        break;
      }
    }

    if (!qrContent) {
      setIsPaymentStarting(false);
      setPaymentError(
        lastStatus === "failed"
          ? "Оплата не прошла. Попробуйте ещё раз."
          : "Не удалось получить ссылку для оплаты. Попробуйте ещё раз.",
      );
      return;
    }

    window.location.href = qrContent;
  }, [activePlayerId, activeProduct, cleanupCardWidget, isPaymentStarting]);

  return {
    activeProduct,
    isMethodSelectorOpen,
    isCardOverlayOpen,
    isPaymentStarting,
    paymentError,
    cardPaymentKey,
    openMethodSelector,
    closeMethodSelector,
    closePaymentOverlay,
    startCardPayment,
    startSbpPayment,
  };
}
